import { Link } from 'react-router-dom';
import { Users, Target, Shield, ArrowUpRight } from 'lucide-react';

export default function BlogPolymarketChoosingTraders() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-8 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto space-y-10 animate-fade-in">
        <header className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-600/15 border border-sky-500/40 text-xs text-sky-100 uppercase tracking-wide">
            <Users className="w-3 h-3" />
            <span>Trader selection</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            How to Choose Polymarket Traders to Copy (Without Getting Farmed)
          </h1>
          <p className="text-dark-300 text-sm sm:text-base max-w-2xl">
            Copy trading only works when you copy the right behavior. This guide shows you a practical way
            to pick Polymarket traders to mirror on BetMimic: what signals matter, what stats are traps,
            and how to avoid the classic &quot;one lucky week&quot; accounts.
          </p>
        </header>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-400" />
            The goal: copy a process, not a screenshot
          </h2>
          <p className="text-sm text-dark-300">
            The best Polymarket traders aren&apos;t just right sometimes. They repeatedly show good process:
            they enter with intent, avoid dead markets, manage size, and take exits when the edge is gone.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>
              Prefer traders who trade a repeatable category (elections, sports, macro) over random viral
              contracts.
            </li>
            <li>
              Prefer traders whose fills look like real execution (consistent sizing, real liquidity) over
              tiny spam trades.
            </li>
            <li>
              Prefer traders who survive drawdowns instead of swinging for a single resolution lottery.
            </li>
          </ul>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-400" />
            A simple 7-point checklist
          </h2>
          <ol className="text-sm text-dark-300 list-decimal list-inside space-y-2">
            <li>
              <span className="font-medium">Volume that isn&apos;t fake</span>: real size across multiple days,
              not one giant bet and then silence.
            </li>
            <li>
              <span className="font-medium">Not just longshots</span>: if they only buy 1–5% lines, you are
              basically paying to watch variance.
            </li>
            <li>
              <span className="font-medium">Liquidity awareness</span>: they don&apos;t constantly trade markets
              with no depth.
            </li>
            <li>
              <span className="font-medium">Timing</span>: entries show up before the crowd, not after the
              headline is already priced.
            </li>
            <li>
              <span className="font-medium">Exit behavior</span>: they reduce/close positions instead of
              holding everything to settlement.
            </li>
            <li>
              <span className="font-medium">Risk concentration</span>: they don&apos;t stack ten correlated markets
              that are really one bet.
            </li>
            <li>
              <span className="font-medium">Consistency</span>: they show up week after week.
            </li>
          </ol>
        </section>

        <section className="card space-y-4">
          <h2 className="text-xl font-semibold">How to copy them safely in BetMimic</h2>
          <p className="text-sm text-dark-300">
            Even the best trader can have a bad month. Your job is to cap the damage.
          </p>
          <ul className="text-sm text-dark-300 list-disc list-inside space-y-2">
            <li>Start with small fixed sizing and scale only after you see clean fills.</li>
            <li>Set a max exposure cap so one trader can&apos;t dominate your bankroll.</li>
            <li>Use price filters (min/max) so you don&apos;t chase late, illiquid entries.</li>
            <li>Use max open positions to avoid being sprayed across 50 micro markets.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              Read more Polymarket guides
            </Link>
            <Link
              to="/traders"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/90 hover:bg-primary-500 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              Browse traders
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
